import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useConfig } from '../../contexts/ConfigContext';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { Sparkles, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import Footer from '../../components/Footer';
import './PublicPaymentPage.css';

const PublicPaymentPage = () => {
    useIframeCommunication();
    const { config, loading: configLoading } = useConfig();

    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [therapistId, setTherapistId] = useState('');
    const [sessionDate, setSessionDate] = useState('');
    const [amount, setAmount] = useState('');
    const [agree, setAgree] = useState(false);

    const isMag = config?.code === 'mag';
    const privacyUrl = config?.privacyPolicyUrl || '#';
    const offerUrl = config?.offerTermsUrl || config?.website || '#';

    const { data: therapistsData, error: therapistsError, isLoading: therapistsLoading } = useQuery(
        ['publicPaymentTherapists'],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/paykeeper/payment-therapists`);
            return res.data;
        },
        { enabled: !configLoading && isMag, retry: 1 }
    );

    const previewMutation = useMutation(
        async (payload) => {
            const res = await axios.post(`${API_BASE_URL}/paykeeper/invoice/preview`, payload);
            return res.data;
        },
        {
            onSuccess: (data) => {
                const url = data.paymentUrl || data.invoice_url || data.url || data.link;
                if (url) {
                    toast.success('Переход к оплате…');
                    window.open(url, '_blank', 'noopener,noreferrer');
                } else {
                    toast.success('Запрос выполнен');
                }
            },
            onError: (err) => {
                const msg = err.response?.data?.message || err.message || 'Не удалось создать счёт';
                toast.error(msg);
            },
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!agree) {
            toast.error('Подтвердите согласие с политикой и офертой');
            return;
        }
        previewMutation.mutate({
            lastName,
            firstName,
            middleName,
            email,
            phone,
            therapistId: therapistId ? parseInt(therapistId, 10) : '',
            sessionDate,
            amount,
            agreePersonalData: true,
        });
    };

    const therapists = therapistsData?.items || [];
    const listError =
        therapistsError?.response?.status === 403
            ? 'Оплата через этот сервис доступна только в организации МАГ.'
            : therapistsError?.response?.data?.message || null;

    if (configLoading) {
        return (
            <div className="public-payment-page">
                <div className="public-payment-inner">
                    <p className="public-payment-unavailable">Загрузка…</p>
                </div>
            </div>
        );
    }

    if (!isMag) {
        return (
            <div className="public-payment-page">
                <div className="public-payment-inner">
                    <div className="public-payment-unavailable">
                        <p>Страница оплаты для этой организации не настроена.</p>
                        <Link to="/" className="public-payment-back">
                            <ArrowLeft size={18} />
                            На главную
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="public-payment-page">
            <div className="public-payment-inner">
                <div className="public-payment-hero">
                    <div className="public-payment-hero-badge">
                        <ShieldCheck size={16} aria-hidden />
                        Безопасная оплата
                    </div>
                    <h1>Оплата консультации</h1>
                    <p>Заполните форму — вы перейдёте на защищённую страницу платёжной системы PayKeeper.</p>
                </div>

                {listError && <div className="public-payment-error">{listError}</div>}

                <div className="public-payment-card">
                    <form className="public-payment-form" onSubmit={handleSubmit}>
                        <div className="public-payment-row2">
                            <div className="public-payment-field">
                                <label htmlFor="pp-lastName">
                                    Фамилия<span className="req">*</span>
                                </label>
                                <input
                                    id="pp-lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                    placeholder="Иванова"
                                />
                            </div>
                            <div className="public-payment-field">
                                <label htmlFor="pp-firstName">
                                    Имя<span className="req">*</span>
                                </label>
                                <input
                                    id="pp-firstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                    placeholder="Мария"
                                />
                            </div>
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-middleName">Отчество</label>
                            <input
                                id="pp-middleName"
                                value={middleName}
                                onChange={(e) => setMiddleName(e.target.value)}
                                autoComplete="additional-name"
                                placeholder="При наличии"
                            />
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-email">
                                Ваш email<span className="req">*</span>
                            </label>
                            <input
                                id="pp-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-phone">
                                Ваш телефон<span className="req">*</span>
                            </label>
                            <input
                                id="pp-phone"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                required
                                autoComplete="tel"
                                placeholder="+7 …"
                            />
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-therapist">
                                Выберите терапевта<span className="req">*</span>
                            </label>
                            <select
                                id="pp-therapist"
                                value={therapistId}
                                onChange={(e) => setTherapistId(e.target.value)}
                                required
                                disabled={therapistsLoading || therapists.length === 0}
                            >
                                <option value="">
                                    {therapistsLoading ? 'Загрузка…' : '— Выберите из списка —'}
                                </option>
                                {therapists.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                            {!therapistsLoading && therapists.length === 0 && !listError && (
                                <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                    Нет терапевтов с заполненными ИНН и телефоном. Обратитесь в администрацию.
                                </p>
                            )}
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-session">
                                Дата сессии<span className="req">*</span>
                            </label>
                            <input
                                id="pp-session"
                                type="date"
                                value={sessionDate}
                                onChange={(e) => setSessionDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="public-payment-field">
                            <label htmlFor="pp-amount">
                                Сумма к оплате (₽)<span className="req">*</span>
                            </label>
                            <input
                                id="pp-amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                placeholder="0.00"
                            />
                        </div>

                        <div className="public-payment-consent">
                            <input
                                id="pp-agree"
                                type="checkbox"
                                checked={agree}
                                onChange={(e) => setAgree(e.target.checked)}
                            />
                            <label htmlFor="pp-agree">
                                Соглашаюсь с{' '}
                                <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
                                    политикой обработки персональных данных
                                </a>{' '}
                                и{' '}
                                <a href={offerUrl} target="_blank" rel="noopener noreferrer">
                                    условиями оферты
                                </a>
                                .
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="public-payment-submit"
                            disabled={
                                previewMutation.isLoading ||
                                therapists.length === 0 ||
                                !agree
                            }
                        >
                            {previewMutation.isLoading ? (
                                'Создание счёта…'
                            ) : (
                                <>
                                    <Sparkles size={18} aria-hidden />
                                    Перейти к оплате
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <Link to="/public/therapists" className="public-payment-back">
                    <ArrowLeft size={18} />
                    К списку психологов
                </Link>
            </div>
            <Footer />
        </div>
    );
};

export default PublicPaymentPage;
